import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { TextField, Typography } from '@cfa/react-components';
import { Radio, RadioGroup } from '@cfa/react-core';

import StepWizardPopUp, {
  type StepWizardInstance,
} from 'components/popups/StepWizardPopUp';

import { OperatorDto } from '@/_generated/api-clients/xp';

interface AddQuizPopUpProps {
  isLicenseeUser: boolean;
  open: boolean;
  setOpen: (open: boolean) => void;
  operators: OperatorDto[];
  submitHandler: ({
    operator,
    quizNameInput,
  }: {
    operator?: OperatorDto;
    quizNameInput: string;
  }) => void;
}

const AddQuizPopUp: React.FC<AddQuizPopUpProps> = ({
  isLicenseeUser,
  open,
  setOpen,
  operators,
  submitHandler,
}) => {
  const isMultiOperator = operators.length > 1 && !isLicenseeUser;
  const initialStep = isMultiOperator ? 1 : 2;
  const [operator, setOperator] = useState<OperatorDto>();
  const [operatorSelected, setOperatorSelected] = useState(false);
  const { t } = useTranslation();
  const [stepIndex, setStepIndex] = useState(initialStep);
  const [stepWizard, setStepWizard] = useState<StepWizardInstance | null>(null);
  const [quizNameInput, setQuizNameInput] = useState('');

  const operatorsNameArray = operators.flat().map(({ name }) => name);

  useEffect(() => {
    if (operators.length && operators.length === 1) {
      setOperator(operators[0]);
    }
  }, [operators]);

  useEffect(() => {
    if (isMultiOperator) {
      setOperator(undefined);
    }
    setStepIndex(initialStep);
    setOperatorSelected(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialStep, open]);

  const closePopup = () => {
    setStepIndex(initialStep);
    setOpen(false);
  };

  const selectOperator = (operatorName: string) => {
    const selectedOperator = operators.find(
      ({ name }) => name === operatorName,
    );
    setOperator(selectedOperator);
    setOperatorSelected(true);
  };

  const Step1 = () => (
    <>
      <Typography variant="h3" className="mb-4!">
        {t('TrainingPlans.buildQuizzes.chooseOperator')}
      </Typography>

      <div className="flex justify-center">
        <RadioGroup
          defaultValue=""
          orientation="vertical"
          onChange={selectOperator}
        >
          {operatorsNameArray
            .filter(operatorName => operatorName !== undefined)
            .map(operatorName => (
              <Radio value={operatorName} key={operatorName + 'quiz'}>
                {operatorName}
              </Radio>
            ))}
        </RadioGroup>
      </div>
    </>
  );

  const Step2 = () => (
    <>
      <Typography variant="h3" className="mb-4!">
        {t('Generic.giveName', { object: t('Generic.quiz') })}
      </Typography>
      <TextField
        data-testid="InputQuizName"
        fullWidth
        onChange={e => setQuizNameInput(e.target.value)}
        onKeyDown={e =>
          e.key === 'Enter' && submitHandler({ operator, quizNameInput })
        }
        placeholder={t('TrainingPlans.buildQuizzes.quizNamePlaceholder')}
        value={quizNameInput}
      />
    </>
  );

  const stepsData = {
    1: {
      labelButtonDeny: t('Button.cancel'),
      labelButtonNext: t('Button.next'),
      onClickBack: () => {
        closePopup();
      },
      onClickNext: () => {
        stepWizard?.nextStep();
      },
      isDisabledButtonNext: !operatorSelected,
      ContentData: Step1,
    },
    2: {
      labelButtonDeny: t('Button.back'),
      labelButtonNext: t('Button.create'),
      onClickBack: () => {
        if (isMultiOperator) {
          return stepWizard?.previousStep();
        }

        closePopup();
      },

      onClickNext: () => {
        submitHandler({ operator, quizNameInput });
      },
      isDisabledButtonNext: !quizNameInput,
      ContentData: Step2,
    },
  };

  const step = stepsData[stepIndex as keyof typeof stepsData];

  return (
    <StepWizardPopUp
      closePopup={closePopup}
      initialStep={initialStep}
      open={open}
      setStepIndex={setStepIndex}
      setStepWizard={setStepWizard}
      step={step}
      steps={stepsData}
    />
  );
};

export default AddQuizPopUp;
